import React, { useEffect, useState } from "react";
import { useEmployees } from "../../api/queries/employeeQueries";
import { employeeName } from "../../utils/text";
import { useAllCompanies } from "../layout/WorkingCompany";

/**
 * Search employees of one company (existing employee list API) and pick one.
 * value: selected employee object or null.
 * excludeId: one id or an array of ids to leave out (e.g. the employee being evaluated).
 */
const EmployeePicker = ({
  companyId,
  value,
  onChange,
  excludeId,
  disabled,
  autoFocus,
  placeholder,
  id,
}) => {
  const [search, setSearch] = useState("");
  const [debounced, setDebounced] = useState("");

  useEffect(() => {
    const t = setTimeout(() => setDebounced(search.trim()), 300);
    return () => clearTimeout(t);
  }, [search]);

  // No company: a super admin on "All companies" searches every company (GET /employee)
  const allCompanies = useAllCompanies();
  const canSearch = !!companyId || allCompanies;
  const query = useEmployees(
    { companyId: companyId || undefined, search: debounced, page: 1, limit: 8 },
    { enabled: canSearch && debounced.length >= 2 && !value },
  );
  const excluded = [].concat(excludeId || []);
  const options = (query.data?.data || []).filter(
    (e) => !excluded.includes(e.id),
  );

  if (value) {
    return (
      <div className="employee-picked">
        <div>
          <div className="fw-semibold text-navy">{employeeName(value)}</div>
          <div className="text-slate-500 fs-075">{value.employeeId}</div>
        </div>
        {!disabled && (
          <button
            type="button"
            className="btn btn-sm btn-link text-slate-600"
            onClick={() => onChange(null)}
          >
            Change
          </button>
        )}
      </div>
    );
  }

  return (
    <div className="employee-picker">
      <div className="position-relative">
        <i className="bi bi-search position-absolute top-50 translate-middle-y ms-3 text-slate-500 fs-080"></i>
        <input
          type="search"
          className="form-control ps-5"
          id={id}
          placeholder={
            placeholder ||
            "Type at least 2 letters of a name or employee number"
          }
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          disabled={disabled || !canSearch}
          autoFocus={autoFocus}
          aria-label="Search employee"
        />
      </div>
      {debounced.length >= 2 && (
        <ul className="employee-picker-results" role="listbox">
          {query.isFetching && options.length === 0 && (
            <li className="text-slate-500">Searching…</li>
          )}
          {!query.isFetching && options.length === 0 && (
            <li className="text-slate-500">No employees found.</li>
          )}
          {options.map((e) => (
            <li key={e.id}>
              <button
                type="button"
                role="option"
                aria-selected="false"
                onClick={() => onChange(e)}
              >
                <span className="fw-semibold">{employeeName(e)}</span>
                <span className="text-slate-500 fs-075 ms-2">
                  {e.employeeId}
                </span>
                {allCompanies && (e.company?.companyName || e.companyName) && (
                  <span className="text-slate-400 fs-070 ms-2">
                    {e.company?.companyName || e.companyName}
                  </span>
                )}
                {e.employeeStatus && (
                  <span className="text-slate-400 fs-070 ms-auto">
                    {e.employeeStatus}
                  </span>
                )}
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};

export default EmployeePicker;
